import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { EASE } from "../../lib/site";

export const Modal = ({ open, title, onClose, children }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          data-testid="modal-backdrop"
          className="fixed inset-0 z-[80] bg-ink/60 backdrop-blur-sm md:flex md:items-center md:justify-center md:p-6"
        >
          <motion.div
            key="panel"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 44, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.45, ease: EASE }}
            data-testid="modal-panel"
            className="relative flex h-[100dvh] w-full touch-auto flex-col overflow-hidden bg-paper shadow-[0_40px_90px_-20px_rgba(26,26,26,0.55)] md:h-auto md:max-h-[88dvh] md:w-[min(960px,96vw)] md:rounded-[28px]"
          >
            <div className="flex shrink-0 flex-col gap-1 border-b border-ink/10 bg-paper px-4 py-3 sm:px-8 sm:py-4">
              <div className="flex items-center justify-end">
                <button onClick={onClose} data-testid="modal-close-btn" aria-label="Close" className="grid h-12 w-12 place-items-center rounded-full bg-ink text-paper transition-transform duration-300 hover:scale-110">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <span data-testid="modal-title" className="px-1 font-serif text-xl leading-snug text-ink sm:px-2 sm:text-2xl">{title}</span>
            </div>
            {/* data-lenis-prevent: Lenis is stopped while a modal is open, and a stopped
                Lenis calls preventDefault() on every touchmove/wheel on the page.
                This attribute makes Lenis ignore events inside this scroller so the
                browser's native (touch) scrolling works on iOS Safari / Android Chrome. */}
            <div data-lenis-prevent className="modal-scroll modal-scope min-h-0 flex-1 px-5 pt-2 sm:px-8">
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
